import { NavLink, Outlet } from 'react-router-dom';
import { 
  LayoutDashboard, 
  History, 
  FlaskConical,
  Settings,
  ChevronLeft,
  ChevronRight,
  Building,
  Stethoscope,
  Users,
  Shield,
  Globe,
  Check,
  Menu,
  X,
  Calendar
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { cn } from '../lib/utils';
import { Toaster } from './ui/sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';
import { useTranslation } from '../contexts/TranslationContext';
import { useTheme } from '../contexts/ThemeContext';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_eldera-care/artifacts/m9394yp0_Original_Logo_Symbol-removebg-preview%20%281%29.png";

const languages = [
  { code: 'EN', label: 'English', flag: '🇬🇧' },
  { code: 'FR', label: 'Français', flag: '🇫🇷' },
  { code: 'DE', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'LV', label: 'Latviešu', flag: '🇱🇻' },
  { code: 'LT', label: 'Lietuvių', flag: '🇱🇹' },
];

export function Layout() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { t, language, setLanguage } = useTranslation();
  const { isDark } = useTheme();

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setMobileMenuOpen(false);
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close mobile menu on navigation
  const handleNavClick = () => {
    if (isMobile) {
      setMobileMenuOpen(false);
    }
  };

  const navItems = [
    { to: '/', icon: LayoutDashboard, label: t('dashboard') },
    { to: '/appointments', icon: Calendar, label: t('appointments') },
    { to: '/logs', icon: History, label: t('logs') },
    { to: '/simulation', icon: FlaskConical, label: t('simulation') },
    { to: '/general-settings', icon: Settings, label: t('general_settings') },
  ];

  const viewItems = [
    { icon: Building, label: t('concierge'), active: true },
    { icon: Users, label: t('family'), active: false },
    { icon: Stethoscope, label: t('doctor'), active: false },
    { icon: Shield, label: t('medical_staff'), active: false },
  ];

  const SidebarContent = ({ mobile = false }) => (
    <>
      {/* Logo */}
      <div className={cn(
        "h-16 flex items-center justify-between px-4 border-b border-[#27272a]",
        mobile && "pr-2"
      )}>
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="Eldera" className="h-8 w-8 object-contain" />
          <div className="flex flex-col">
            <span className="font-barlow font-bold text-sm tracking-tight">ELDERA.LV</span>
            <span className="text-[10px] text-[#52525b]">Safe Platform</span>
          </div>
        </div>
        {mobile ? (
          <button 
            onClick={() => setMobileMenuOpen(false)}
            className="p-2 rounded-md hover:bg-[#27272a] transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        ) : (
          <button 
            onClick={() => setCollapsed(!collapsed)}
            className="p-1.5 rounded-md hover:bg-[#27272a] transition-colors"
            data-testid="sidebar-toggle"
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </button>
        )}
      </div>

      {/* Views Section */}
      <div className="px-3 py-3 border-b border-[#27272a]">
        <div className="text-[10px] text-[#52525b] uppercase tracking-wider mb-2 px-3">{t('views')}</div>
        <div className="space-y-1">
          {viewItems.map(({ icon: Icon, label, active }) => (
            <div
              key={label}
              className={cn(
                "flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200",
                active 
                  ? "bg-blue-500/10 text-blue-500 border border-blue-500/20 cursor-pointer" 
                  : "text-[#52525b] cursor-not-allowed opacity-50"
              )}
            >
              <Icon className="h-4 w-4 flex-shrink-0" />
              <span className="text-xs font-medium">{label}</span>
              {active && <Check className="h-3 w-3 ml-auto" />}
            </div>
          ))}
        </div>
      </div>

      {/* Navigation */}
      <nav className="p-3 space-y-1 flex-1">
        <div className="text-[10px] text-[#52525b] uppercase tracking-wider mb-2 px-3">{t('navigation')}</div>
        {navItems.map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            onClick={handleNavClick}
            className={({ isActive }) => cn(
              "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200",
              isActive 
                ? "bg-blue-500/10 text-blue-500 border border-blue-500/20" 
                : "text-[#a1a1aa] hover:text-[#fafafa] hover:bg-[#27272a]"
            )}
          >
            <Icon className="h-5 w-5 flex-shrink-0" />
            <span className="font-medium text-sm">{label}</span>
          </NavLink>
        ))}
      </nav>

      {/* Language Selector */}
      <div className="border-t border-[#27272a] p-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button 
              className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[#27272a] transition-colors w-full"
              data-testid="language-selector"
            >
              <Globe className="h-4 w-4 text-[#a1a1aa]" />
              <span className="text-sm font-medium">{languages.find(l => l.code === language)?.flag} {language}</span>
              <ChevronRight className="h-3 w-3 ml-auto text-[#52525b]" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" side={mobile ? "top" : "right"} className="bg-[#18181b] border-[#27272a]">
            {languages.map(({ code, label, flag }) => (
              <DropdownMenuItem
                key={code}
                onClick={() => setLanguage(code)}
                className={cn(
                  "cursor-pointer",
                  language === code && "bg-blue-500/10 text-blue-500"
                )}
              >
                <span className="mr-2">{flag}</span>
                <span className="font-mono mr-2">{code}</span>
                {label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-[#27272a]">
        <div className="text-xs text-[#52525b]">
          <div className="font-mono">v1.0.0-demo</div>
          <div className="mt-1">{t('concierge')} View</div>
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[var(--eldera-bg)] text-[var(--eldera-text)]">
      {/* Mobile Header */}
      {isMobile && (
        <header className="fixed top-0 left-0 right-0 h-14 bg-[var(--eldera-sidebar)] backdrop-blur-xl border-b border-[var(--eldera-border)] z-40 flex items-center justify-between px-4">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Eldera" className="h-7 w-7 object-contain" />
            <span className="font-barlow font-bold text-sm">ELDERA.LV</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setMobileMenuOpen(true)}
            className="p-2"
            data-testid="mobile-menu-btn"
          >
            <Menu className="h-5 w-5" />
          </Button>
        </header>
      )}

      {/* Mobile Sidebar Overlay */}
      {isMobile && mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/60 z-50 backdrop-blur-sm"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar - Desktop */}
      {!isMobile && (
        <aside 
          className={cn(
            "fixed left-0 top-0 h-full bg-[var(--eldera-sidebar)] backdrop-blur-xl border-r border-[var(--eldera-border)] z-50 transition-all duration-300 flex flex-col",
            collapsed ? "w-16" : "w-64"
          )}
        >
          {collapsed ? (
            // Collapsed sidebar (icons only)
            <>
              <div className="h-16 flex items-center justify-center border-b border-[#27272a]">
                <img src={LOGO_URL} alt="Eldera" className="h-8 w-8 object-contain" />
              </div>
              <nav className="p-2 space-y-1 flex-1">
                {navItems.map(({ to, icon: Icon, label }) => (
                  <NavLink
                    key={to}
                    to={to}
                    title={label}
                    className={({ isActive }) => cn(
                      "flex items-center justify-center p-2.5 rounded-lg transition-all duration-200",
                      isActive 
                        ? "bg-blue-500/10 text-blue-500 border border-blue-500/20" 
                        : "text-[#a1a1aa] hover:text-[#fafafa] hover:bg-[#27272a]"
                    )}
                  >
                    <Icon className="h-5 w-5" />
                  </NavLink>
                ))}
              </nav>
              <div className="p-2 border-t border-[#27272a]">
                <button 
                  onClick={() => setCollapsed(false)}
                  className="flex items-center justify-center p-2 rounded-lg hover:bg-[#27272a] transition-colors w-full"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </>
          ) : (
            <SidebarContent />
          )}
        </aside>
      )}

      {/* Mobile Sidebar Drawer */}
      {isMobile && (
        <aside 
          className={cn(
            "fixed left-0 top-0 h-full w-72 bg-[var(--eldera-card)] border-r border-[var(--eldera-border)] z-50 transition-transform duration-300 flex flex-col",
            mobileMenuOpen ? "translate-x-0" : "-translate-x-full"
          )}
        >
          <SidebarContent mobile />
        </aside>
      )}

      {/* Main Content */}
      <main className={cn(
        "min-h-screen transition-all duration-300",
        isMobile ? "pt-14" : (collapsed ? "ml-16" : "ml-64")
      )}>
        <Outlet />
      </main>

      <Toaster position="top-right" richColors />
    </div>
  );
}
